// Hook + bouton dictée vocale via Whisper
import React, { useState, useRef } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function VoiceDictate({ onTranscript, language = "fr", className = "", title = "Dictée vocale" }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await upload(blob);
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
    } catch (e) {
      alert("Impossible d'accéder au micro : " + e.message);
    }
  }

  function stop() {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  }

  async function upload(blob) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      fd.append("language", language);
      const { data } = await api.post("/ai/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.text) onTranscript(data.text);
    } catch (e) {
      alert("Erreur transcription: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={busy}
      title={title}
      className={`inline-flex items-center justify-center gap-2 px-3 h-9 rounded-sm text-xs font-medium transition-colors ${
        recording
          ? "bg-bad text-white animate-pulse"
          : busy
            ? "bg-obsidian-800 text-obsidian-500"
            : "bg-citron/10 text-citron border border-citron/30 hover:bg-citron/20"
      } ${className}`}
      data-testid="voice-dictate-button"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : recording ? <Square size={14} /> : <Mic size={14} />}
      {busy ? "Transcription…" : recording ? "Arrêter" : "Dicter"}
    </button>
  );
}
